import { connectToCrmWidgetService, Integration } from '@playbooks/action-cards-utils'
import {
  type ConnectToCrmWidgetData,
  ObjectTypes,
  type CrmObject,
  type ConnectToCrmData,
  type ConnectToCrmWidgetValue,
} from '@playbooks/core/src/interfaces/ConnectToCrmWidget'

import type { FunctionComponent } from 'react'
import useHttpService from '../../../hooks/useHttpService'

import { useEffect, useState } from 'react'
import { useTranslation } from '../../../contexts/localization/Localization'
import { type WidgetProps } from '../widgetTypes'
import getDataFromActionCard from '../../../utils/getActionCardForWidget'
import { CheckInCircleGradientIcon, CrossInCircleIcon, RefreshIcon, SparklesGradientIcon } from '../../../components/ui'
import CreateListItem from './CreateListItem'
import SyncLinkItem from './SyncLinkItem'
import FieldWithIcon from './FieldWithIcon'
import IconWrapper from './IconWrapper'
import ValuesList from './ValuesList'
import mapConnectorWidgetValue from '../../../utils/mapConnectorWidgetValue'
import useHandleIntegrationAuth from '../../../hooks/useHandleIntegrationAuth'
import { CustomButton } from '../../../components/ui/CustomButton'
import { useAppContext } from '../../../../src/contexts/AppContext'
import { usePlaybooksBackendClient } from '../../../hooks/usePlaybooksBackendClient'
import useComponentsApiRegistry from '../../../hooks/useComponentsApiRegistry'
import { IWidgetComponentApi } from '@playbooks/action-cards-utils'

interface ConnectToCrmWidgetProps extends WidgetProps {
  widget: ConnectToCrmWidgetData
}

type DataForHandle = {
  creation: CrmObject[]
  sync: CrmObject[]
}

const ConnectToCrmWidget: FunctionComponent<ConnectToCrmWidgetProps> = ({ widget }) => {
  const { data: state, update: updateState, settings, elementsWithCode } = useAppContext()
  const { text: t } = useTranslation()
  const httpService = useHttpService()
  const [isLoading, setIsLoading] = useState(false)
  const [dataForHandle, setDataForHandle] = useState<DataForHandle | null>(null)
  const [statusText, setStatusText] = useState('')
  const { objectsQueryString } = getCrmObjectsMap(widget.crmObjects)
  const [value, setValue] = useState<ConnectToCrmWidgetValue[]>([])
  const staticFields = flattenCrmData(widget.crmObjects)
  const { showAuthBtn, authBtn, handleAuth, isProcessing } = useHandleIntegrationAuth(
    widget.crmSystem.toLowerCase() as Integration,
  )
  const isCustom = !!Number(widget.crmSystem)
  const playbooksBackendClient = usePlaybooksBackendClient()
  const playSource = settings?.playSource ?? 'tenant'

  useEffect(() => {
    if (!value.length) return
    connectToCrmWidgetService(widget).setValue(value)

    updateState(widget, 'value', value)
  }, [value])

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: handleButtonClick,
    } as IWidgetComponentApi
  })

  async function handleButtonClick() {
    if (isLoading || !settings?.playId) {
      return
    }
    const playId = settings?.playId
    if (!playId) {
      setStatusText('Context [Play ID] cannot be determined')
      return
    }

    setIsLoading(true)

    try {
      const data = getDataFromActionCard(state)
      const foundObjects = isCustom
        ? await playbooksBackendClient.integrations.findCrmObjects({
            actionCard: state,
            widgetId: widget.id.toString(),
            playId: playId.toString(),
            playSource,
          })
        : await httpService.findCrmObjects(
            widget.crmSystem,
            objectsQueryString,
            playId,
            data,
            staticFields,
            showAuthBtn,
          )

      const objs =
        foundObjects?.reduce<DataForHandle>(
          (acc, curr) => {
            if (curr.isFound) {
              acc.sync.push(curr)
            } else {
              acc.creation.push(curr)
            }

            return acc
          },
          { creation: [], sync: [] },
        ) || null

      setDataForHandle(objs)
    } catch (error: any) {
      console.error('[ConnectToCrmWidget]:', error)
      setStatusText(t('findObjectsErrorStatus'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleObjects = async () => {
    const playId = settings?.playId
    if (!playId) return
    if (!dataForHandle) return

    setIsLoading(true)

    try {
      const createdObjects = isCustom
        ? await playbooksBackendClient.integrations.createCrmObjects({
            actionCard: state,
            widgetId: widget.id.toString(),
            playId: playId.toString(),
            playSource,
            objectTypes: dataForHandle.creation.map(x => x.objectType),
          })
        : await httpService.createCrmObjects(
            widget.crmSystem,
            dataForHandle.creation,
            playId,
            getDataFromActionCard(state),
            staticFields,
            elementsWithCode,
            showAuthBtn,
          )

      const dataForSync = [...dataForHandle.sync, ...createdObjects]
      if (isCustom) {
        await playbooksBackendClient.integrations.syncCrmObjects({
          actionCard: state,
          widgetId: widget.id.toString(),
          playId: playId.toString(),
          playSource,
          objects: dataForHandle.sync,
        })
      } else {
        await httpService.syncCrmObjects(widget.crmSystem, playId, dataForSync, showAuthBtn)
      }

      setValue(mapConnectorWidgetValue([...createdObjects, ...dataForHandle.sync]))
      setDataForHandle(null)
    } catch (error: any) {
      setStatusText(t('handleObjectsErrorStatus'))
      console.error(`Error while creating CRM objects: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  const disconnectWidget = () => {
    connectToCrmWidgetService(widget).setValue([])
    setDataForHandle(null)
    updateState(widget, 'value', [])
  }

  return (
    <div title={playSource === 'tenant' ? undefined : t('demoPlayNotSupported')}>
      <div>
        {authBtn.isVisible && (
          <CustomButton size="lg" isLoading={isProcessing} onClick={handleAuth}>
            {authBtn.text} ({widget.buttonLabel})
          </CustomButton>
        )}
        {!authBtn.isVisible && !dataForHandle && !widget.value?.length && (
          <CustomButton
            size="lg"
            isLoading={isLoading}
            disabled={!settings?.playId || playSource !== 'tenant'}
            onClick={handleButtonClick}
            tooltip={settings?.playId ? undefined : t('playIdNotDetermined')}
          >
            <div className={`flex items-center gap-2`}>
              <div className={`-translate-y-px`}>
                <IconWrapper icon={<RefreshIcon />} />
              </div>
              {widget.buttonLabel}
            </div>
          </CustomButton>
        )}
      </div>

      {!!widget.value?.length && (
        <div className="mt-6">
          <ValuesList widget={widget} disconnectWidget={disconnectWidget} />
        </div>
      )}

      {dataForHandle && (
        <div className="flex flex-col rounded-[10px] mt-2">
          <FieldWithIcon
            icon={
              <div className="text-[14px]">
                <SparklesGradientIcon />{' '}
              </div>
            }
            data={t('connectCrmWidgetEstablishText')}
          />

          <div className="flex items-center bg-[#E2E2E2] text-[#1B1B1B] dark:bg-zinc-700 dark:text-white pl-12.5 h-6.75 my-2.5">
            {t('connectCrmWidgetPlannedActions')}:
          </div>

          <div className={`pb-5`}>
            <ul>
              {dataForHandle?.sync.map(obj => {
                return (
                  <li key={obj.id} className={`mb-2.5`}>
                    <SyncLinkItem data={obj} />
                  </li>
                )
              })}
            </ul>

            <ul>
              {dataForHandle?.creation.map(value => {
                return (
                  <li key={value.id}>
                    <CreateListItem crmObject={value} />
                  </li>
                )
              })}
            </ul>
          </div>

          <div className={`flex mt-auto gap-2 ml-auto`}>
            <CustomButton isLoading={isLoading} onClick={handleObjects}>
              <div className={`flex items-center gap-2`}>
                <CheckInCircleGradientIcon />
                {t('proceedLabel')}
              </div>
            </CustomButton>
            <CustomButton onClick={() => setDataForHandle(null)}>
              <div className={`flex items-center gap-2`}>
                <CrossInCircleIcon />
                {t('cancelLabel')}
              </div>
            </CustomButton>
          </div>
        </div>
      )}
      {statusText && (
        <p className="mt-1">
          <span className="text-sm text-gray-500">{statusText}</span>
        </p>
      )}
    </div>
  )
}

function getCrmObjectsMap(crmObjects: ConnectToCrmData) {
  const res = Object.keys(crmObjects).map(key => `${key[0].toUpperCase()}${key.slice(1)}` as ObjectTypes)

  return {
    objectsQueryString: res.join(','),
    objects: res,
  }
}

/**
 * Flattens an object of the form:
 * {
 *   company: { crmData: { … } },
 *   ...
 * }
 * into:
 * {
 *   company: { … },
 *   ...
 * }
 */
function flattenCrmData(input: object): Record<string, object> {
  return Object.fromEntries(
    Object.entries(input).map(([key, wrapper]) => [
      key,
      // if wrapper.crmData exists, use it—otherwise, leave as-is
      wrapper && wrapper.crmData ? wrapper.crmData : wrapper,
    ]),
  )
}

export default ConnectToCrmWidget
